import type {
  CoreClientTasksReadParams,
  CoreCoachTasksCoachReadParams,
} from '../../../api/types/core/methods';
import type {AsyncStrategy, BaseConfig, Batch} from '../utils';

import {taskStore} from '../../../api/task';
import {getEntityTypeById} from '../../../utils/entity/getEntityType';
import {AppError} from '../../../utils/errors/app';

export {
  taskFetch,
  /** @public */
  taskFetchBatch,
};

type BatchConfig = {
  asClient: Batch<ClientConfig>;
  asCoach: Batch<CoachConfig>;
};

type ClientConfig = BaseConfig<CoreClientTasksReadParams>;

type CoachConfig = BaseConfig<CoreCoachTasksCoachReadParams>;

type Config = {
  asClient: ClientConfig;
  asCoach: CoachConfig;
};

const strategy: AsyncStrategy = async (roles, config) => {
  if (isBaseOrCoachTask((config as Config).asCoach.params._id)) {
    return roles.coach(config);
  } else if (isClientTask((config as Config).asClient.params._id)) {
    return roles.client(config);
  } else {
    throw new AppError('Wrong strategy config');
  }
};

const strategyBatch: AsyncStrategy = async (roles, config) => {
  if (
    (config as BatchConfig).asCoach.params.every(item =>
      isBaseOrCoachTask(item._id),
    )
  ) {
    return roles.coach(config);
  } else if (
    (config as BatchConfig).asClient.params.every(item =>
      isClientTask(item._id),
    )
  ) {
    return roles.client(config);
  } else {
    throw new AppError('Wrong strategy batch config');
  }
};

async function taskFetch(config: Config) {
  return strategy({client: asClient, coach: asCoach}, config);
}

taskFetch.asClient = asClient;
taskFetch.asCoach = asCoach;

async function taskFetchBatch(config: BatchConfig) {
  return strategyBatch({client: asClientBatch, coach: asCoachBatch}, config);
}

taskFetchBatch.asClient = asClientBatch;
taskFetchBatch.asCoach = asCoachBatch;

async function asClient(config: Config) {
  return taskStore.client.fetch(config.asClient.params);
}

async function asClientBatch(config: BatchConfig) {
  return taskStore.client.fetchBatch(config.asClient.params);
}

async function asCoach(config: Config) {
  return taskStore.coach.fetch(config.asCoach.params);
}

async function asCoachBatch(config: BatchConfig) {
  return taskStore.coach.fetchBatch(config.asCoach.params);
}

function isBaseOrCoachTask(id: string) {
  return (
    getEntityTypeById(id) === 'coach_task' ||
    getEntityTypeById(id) === 'base_task'
  );
}

function isClientTask(id: string) {
  return getEntityTypeById(id) === 'task';
}
